/* eslint-disable camelcase */
import { observer } from "mobx-react-lite"
import React, { FC, ReactElement, useCallback, useEffect, useMemo, useState } from "react"
import { Appearance, useWindowDimensions, ViewStyle, FlatList, ListRenderItem, ListRenderItemInfo, Linking } from "react-native"
import {
  ActionsheetIosAware,
  ListEmpty,
  Screen,
  Text,
} from "../../../../components"
import numeral from "numeral"
import { useStores } from "../../../../models"
import { BatchStoreModel } from "../../../../models/app-store/farm-store/batch-store/batch-store"
import { FarmStoreModel } from "../../../../models/app-store/farm-store/farm-store"
import { color, nativeBaseTheme } from "../../../../theme"
import { SafeAreaView } from "react-native-safe-area-context"
import { View, Actionsheet, Image, Icon, useDisclose } from "native-base"
import _ from "lodash"
import { TxKeyPath, translate } from "../../../../i18n"
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../../utils/jala-analytic"
import FontAwesome from "react-native-vector-icons/FontAwesome"
import MaterialCommunityIcons from "react-native-vector-icons/MaterialCommunityIcons"
import { TouchableOpacity } from "react-native-gesture-handler"
import { navigate } from "../../../../navigators"
import { getSnapshot } from "mobx-state-tree"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const HEADER: ViewStyle = {
  borderTopWidth: 1,
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  flexDirection: 'row',
  paddingVertical: 8,
  alignItems: 'center',
  backgroundColor: color.palette.white
}

const CONTENT_ITEM: ViewStyle = {
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  flexDirection: 'row'
}

export const InfoWarningModal: FC<{ colorScheme?: "light" | "dark" }> = observer(function InfoWarningModal(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { isOpen, onClose, onOpen } = useDisclose()

  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore } = farmStore || FarmStoreModel.create({})
  const { cyclesFeedActualWithin4Days } = activeBatchStore || BatchStoreModel.create({})

  const ponds = useMemo(() => {
    let newArray = []
    if (cyclesFeedActualWithin4Days.length) {
      newArray = cyclesFeedActualWithin4Days.map((cycle) => cycle.pond_name);
    }
    return newArray
  }, [cyclesFeedActualWithin4Days])

  const firstPond = useMemo(() => {
    if (ponds.length >= 2) {
      if (ponds.length === 2) {
        return ponds.join(' and ');
      } else {
        const firstThree = ponds.slice(0, 3)
        const firstTwo = firstThree.slice(0, 2)
        const lastOne = firstThree[2]
        return ponds.length === 3 ? `${firstTwo.join(', ')} and ${lastOne}` : firstTwo.join(', ');
      }
    } else {
      return ponds;
    }
  }, [ponds])

  const otherPonds = useMemo(() => {
    if(ponds.length > 3) {
      const otherPonds = ponds.slice(2)
      return otherPonds.join(', ')
    }
    return '-'
  }, [ponds])

  return (<>
    <Text>
      <Text text={translate("overviewScreen.disclaimerWithPond", { ponds: firstPond })} preset="caption2" colorScheme={colorScheme} color={color.palette.red700} />
      {ponds.length > 3 && <TouchableOpacity onPress={onOpen}>
        <Text text={translate("overviewScreen.disclaimerButton", { number: ponds.length - 2 })} preset="caption2" colorScheme={colorScheme} color={color.palette.red700} underline />
      </TouchableOpacity>}
      <Text tx="overviewScreen.disclaimerWithPondEnd" preset="caption2" colorScheme={colorScheme} color={color.palette.red700} />
    </Text>
    <ActionsheetIosAware onClose={onClose} isOpen={isOpen}>
      <Actionsheet.Content alignItems="stretch" px={0} py={0}>
        <View
          padding="16px"
          rounded="8px"
          backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
        >
          <Text text={otherPonds} preset="body2" colorScheme={colorScheme} color={color.palette.gray700} />
        </View>
        <SafeAreaView edges={['bottom']} />
      </Actionsheet.Content>
    </ActionsheetIosAware>
  </>)
})

const Header: FC<{ colorScheme?: "light"|"dark" }> = observer(function Header(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore } = farmStore || FarmStoreModel.create({})
  const { totalFeedCost, cycles, loadingCyclePrediction, isFeedActualWithin4Days } = activeBatchStore || BatchStoreModel.create({})

  return (
    <View paddingX="16px" paddingTop="8px" paddingBottom="15px">
      <Text text={farmStore.farm.name} preset="title3" marginBottom="4px" colorScheme={colorScheme} />
      <Text tx="overviewFeedCostListScreen.subtitle" preset="body3" colorScheme={colorScheme} color={nativeBaseTheme.colors.gray[colorScheme === 'light' ? '700' : '300']} />
      <View flexDirection="row" alignItems="center">
        <Text
          text={numeral(totalFeedCost / 1000000).format('0,0.[00]')}
          preset="display1"
          marginRight="3px"
          colorScheme={colorScheme}
          color={loadingCyclePrediction && color.palette.dust500 || undefined}
        />
        <Text tx="million" preset="display2" marginRight="8px" colorScheme={colorScheme} color={color.palette.dust500} />
      </View>
      <Text text={translate("overviewScreen.fromActivePonds", { count: cycles.length })} preset="body3" colorScheme={colorScheme} color={color.palette.dust500} />
      {!isFeedActualWithin4Days && <InfoWarningModal />}
    </View>
  )
})

const TableHeader: FC<{ colorScheme?: "light"|"dark", onPressMoreInfo: () => void }> = (props) => {
  const { onPressMoreInfo } = props;


  return (
    <View style={HEADER}>
      <View width="25%" paddingX="8px" alignItems="flex-start">
        <Text tx="overviewFeedCostListScreen.pond" preset="body3" color={color.palette.gray800} />
      </View>
      <View width="30%" paddingX="8px" alignItems="flex-end">
        <Text tx="overviewFeedCostListScreen.feedCost" preset="body3" color={color.palette.gray800} />
      </View>
      <View width="20%" paddingX="8px" alignItems="flex-end">
        <Text tx="overviewFeedCostListScreen.feed" preset="body3" color={color.palette.gray800} />
      </View>
      <TouchableOpacity
        style={BUTTON_UPDATE_FEED_PRICE}
        onPress={onPressMoreInfo}
      >
        <Text tx="overviewFeedCostListScreen.price" preset="body3" color={color.palette.gray800} marginRight={"2px"} />
        <MaterialCommunityIcons name="information-outline" size={16} color={color.palette.gray700} />
      </TouchableOpacity>
    </View>
  )
}

const BUTTON_UPDATE_FEED_PRICE: ViewStyle = {
  width: '50%',
  paddingHorizontal: 8,
  paddingVertical: 10,
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexDirection: 'row',
}

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any> }> = observer(function Item(props) {
  const { colorScheme, info } = props
  const [feedCost, setFeedCost] = useState(0)
  const [feedAccumulation, setFeedAccumulation] = useState(0)

  const { item: { age, pond_name, feed_price, cycle_actuals, cycle_predictions}, item } = info;
  useEffect(() => {
    const actualCycle = cycle_actuals.find((c) => c.age === age) || {}
    if (actualCycle.feed_cost) {
      setFeedCost(actualCycle.feed_cost)
      setFeedAccumulation(actualCycle.feed_accumulation)
    }
    // else {
    //   const predictionCycle = cycle_predictions.find((c) => c.age === age) || {}
    //   setFeedCost(predictionCycle.feed_cost)
    //   setFeedAccumulation(predictionCycle.feed_accumulation)
    // }
  }, [age, cycle_actuals, cycle_predictions])

  const onPressUpdatePrice = useCallback(() => {
    JalaAnalytic.log('Mobile app: Home - Overview - Feed cost - Feed price - Click');
    navigate('Home.Stack', 'Home.Cycle.EditMultiple', { models: [item], scrollToFeedForm: true })
  }, [item])

  return (
    <View style={CONTENT_ITEM}>
      <View width="25%" paddingX="8px" py="10px" alignItems="flex-start">
        <Text ellipsizeMode="middle" text={`${pond_name} (${age})`} preset="body3" colorScheme={colorScheme} numberOfLines={1} />
      </View>
      <View width="30%" paddingX="8px" py="10px" alignItems="flex-end" backgroundColor={color.palette.gray100}>
        <Text text={numeral(feedCost).format('0,0')} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="20%" paddingX="8px" py="10px" alignItems="flex-end">
        <Text text={numeral(feedAccumulation).format('0,0')} preset="body3" colorScheme={colorScheme} />
      </View>
      <TouchableOpacity
        style={BUTTON_UPDATE_FEED_PRICE}
        onPress={onPressUpdatePrice}
      >
        <Text text={numeral(feed_price).format('0,0')} preset="body3" colorScheme={colorScheme} marginRight={"2px"} />
        <FontAwesome name="chain" size={12} color={color.palette.blue500} />
      </TouchableOpacity>
    </View>
  )
});

const FOOTER_STYLE: ViewStyle = {
  borderTopColor: color.palette.lineLight,
  borderTopWidth: 1,
  backgroundColor: color.palette.orange300
}

export const ItemFooter: FC<{ colorScheme?: "light" | "dark" }> = observer(function ItemFooter(props) {
  const { colorScheme } = props

  const onPressSubmitFeedback = useCallback(() => {
    Linking.openURL("https://airtable.com/app9ZXx0EbJOOii4v/shrBVKWGSmy9iR19h").catch()
    JalaAnalytic.log('Mobile App : COGS - Give Feedback');
  }, [])

  return (
    <View style={FOOTER_STYLE}>
      <View paddingTop={"8px"} paddingX={"16px"} paddingBottom={"8px"} backgroundColor={color.palette.white}>
        <Text preset="body3" colorScheme={colorScheme}>
          {translate("overviewProductionCostEstimationListScreen.disclaimerElectricCost").split(/(<.*?>.*?<\/.*?>)/g).map((value, index) => {
            if (value.match(/(<.*?>.*?<\/.*?>)/g)) {
              return <Text colorScheme={colorScheme} key={`key-disclaimer-${index}`} preset="title5">{value.replace(/<.*?>|<\/.*?>/g, "")}</Text>
            }
            return value
          })}
        </Text>
        <Text preset="body3" colorScheme={colorScheme} tx="overviewProductionCostEstimationListScreen.disclaimer" />
      </View>
      <View flexDirection={"row"} height={"28px"} marginX={"16px"} justifyContent={"flex-start"} alignItems={"center"}>
          <Icon as={MaterialCommunityIcons} name="information-outline" size={"18px"} color={color.palette.gray800} />
          <Text paddingLeft={"2px"} justifyContent="space-between" mr="16px" numberOfLines={2}>
            <Text preset="body3" colorScheme={colorScheme} tx="overviewProductionCostEstimationListScreen.feedbackInfo" />
            <Text text=" " />
            <Text onPress={onPressSubmitFeedback} preset="title5" color={color.palette.blue500} tx="overviewProductionCostEstimationListScreen.submitFeedback" />
          </Text>
      </View>
      <SafeAreaView edges={['bottom']} />
    </View>
  )
})

const INFO_ITEM_STYLE: ViewStyle = {
  backgroundColor: color.palette.orange300,
  borderWidth: 1,
  borderColor: color.palette.orange400,
  borderRadius: 4,
  paddingHorizontal: 8,
  paddingVertical: 4,
  marginBottom: 12,
  marginHorizontal: 16,
}

export const InfoItem: FC<{ colorScheme?: "light" | "dark", tx?: TxKeyPath, text?: string | ReactElement, onPressMoreInfo?: () => void }> = observer(function InfoItem(props) {
  const { colorScheme, tx, text, children, onPressMoreInfo } = props
  return (
    <View style={INFO_ITEM_STYLE}>
      {children}
      {!children && <Text color={color.palette.orange700} preset="title5" tx={tx} colorScheme={colorScheme}>{text}</Text>}
      {onPressMoreInfo && <Text marginTop={"4px"} onPress={onPressMoreInfo} color={color.palette.blue600} preset="title5" colorScheme={colorScheme} tx="overviewFeedCostListScreen.moreInfo" />}
    </View>
  )
})

const infoFeedCostImage = require('./info-feed-cost.png')

const InfoModal: FC<{ colorScheme?: "light" | "dark", isOpen?: boolean, onClose?: () => void }> = observer(function InfoModal(props) {
  const { colorScheme, isOpen, onClose } = props
  const dimensions = useWindowDimensions();

  return (
    <ActionsheetIosAware onClose={onClose} isOpen={isOpen}>
      <Actionsheet.Content>
        <View justifyContent={"flex-start"}>
          <Image alt="info-image" width={dimensions.width - 32} height={`${((dimensions.width - 32))/328 * 183}px`} source={infoFeedCostImage} resizeMethod="resize" resizeMode="contain" />
          <Text paddingTop={"8px"} colorScheme={colorScheme} preset="display3" tx="overviewFeedCostListScreen.price" />
          <Text paddingTop={"8px"} colorScheme={colorScheme} preset="body2" tx="overviewFeedCostListScreen.infoPriceText" />
          <Text colorScheme={colorScheme} preset="title4" tx="overviewFeedCostListScreen.infoPriceText2" />
        </View>
        <SafeAreaView edges={['bottom']} />
      </Actionsheet.Content>
    </ActionsheetIosAware>
  )
})

export const OverviewFeedCostListScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function OverviewFeedCostListScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore } = farmStore || FarmStoreModel.create({})
  const { cycles, loadingCyclePrediction } = activeBatchStore || BatchStoreModel.create({})
  const keyExtractor = useCallback((item, index) => `${item.id || item.started_at || index}`, [])
  const [isOpenInfo, setOpenInfo] = useState<boolean>(false)
  const [showHeader, setShowHeader] = useState<boolean>(false)

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} />
  }, [colorScheme])

  const onRefresh = useCallback(() => {
    activeBatchStore.init()
  }, [activeBatchStore])

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: Home - Overview - Feed cost - View');
  }, []))

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      {showHeader && <TableHeader onPressMoreInfo={() => {setOpenInfo(true)}} colorScheme={colorScheme} />}
      <FlatList
        data={getSnapshot(cycles)}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListHeaderComponent={<View backgroundColor={color.palette.white}>
          <Header colorScheme={colorScheme} />
          <InfoItem tx="overviewFeedCostListScreen.infoCostFormula" colorScheme={colorScheme} />
          <TableHeader onPressMoreInfo={() => {setOpenInfo(true)}} colorScheme={colorScheme} />
        </View>}
        ListEmptyComponent={<ListEmpty colorScheme={colorScheme} height={`${height - 270}px`} />}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onRefresh={onRefresh}
        refreshing={!!loadingCyclePrediction}
        onScroll={({nativeEvent}) => {
          setShowHeader(nativeEvent.contentOffset.y>200)
        }}
      />
      <ItemFooter colorScheme={colorScheme} />
      <InfoModal colorScheme={colorScheme} isOpen={isOpenInfo} onClose={() => setOpenInfo(false)} />
    </Screen>
  )
})
